<template>
  <div class="min-h-screen flex flex-col bg-white">
    <!-- Real bug fixed 2026-09-09 (PageSpeed: "Hydration completed but
         contains mismatches", reproduced live via a debug build hydrating
         the actual production prerendered snapshot): this was
         `v-if="hasCrashed"` with the rest of the app as its `v-else`
         Fragment alternative. Vue's hydration algorithm warned "expected
         Symbol(v-fgt)" here specifically because it must adopt whichever
         shape the server-rendered snapshot happened to have (this
         component's own single root, in the fatal-crash case that never
         actually occurs during prerendering) against a client render that
         could, in principle, take the OTHER branch's Fragment shape --
         an unconditional structural mismatch risk baked into having any
         v-if/v-else pair with a differently-shaped alternative at this
         level, not something that depended on hasCrashed's actual value.
         v-show keeps this always mounted (now a real fixed/inset-0
         overlay -- see its own comment) so the app's root always renders
         the SAME shape regardless of hasCrashed, and CrashFallback simply
         toggles visibility on top of it. -->
    <CrashFallback v-show="hasCrashed" :detail="crashDetail" />
    <NavBar v-if="!hideNav" />
    <main class="flex-1">
      <template v-if="pageError">
        <div class="container-wide py-20 text-center">
          <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-warn-50 flex items-center justify-center">
            <Icon name="alert-triangle" :size="28" classes="text-warn-600" />
          </div>
          <h2 class="text-xl font-semibold text-ink-900 mb-2">Something went wrong</h2>
          <p class="text-sm text-ink-600 mb-6 max-w-md mx-auto">{{ pageError }}</p>
          <button class="btn-primary" @click="handlePageReload">
            Reload page
          </button>
        </div>
      </template>
      <router-view v-else v-slot="{ Component, route }">
        <transition name="page-fade" mode="out-in">
          <component :is="Component" :key="route.fullPath" />
        </transition>
      </router-view>
    </main>
    <AppFooter v-if="!hideNav" />
    <ToastHost />
    <AIChatWidget v-if="showChat" />
    <OnboardingWizard
      :visible="showOnboarding"
      :tier="userStore.subscriptionTier"
      :reports-remaining="userStore.reportsRemaining"
      @complete="completeOnboarding"
      @dismiss="dismissOnboarding"
    />
  </div>
</template>

<script setup>
import { onMounted, nextTick, computed, provide, watch, ref, onErrorCaptured } from 'vue';
import { useRoute } from 'vue-router';
import NavBar from './components/layout/NavBar.vue';
import AppFooter from './components/layout/AppFooter.vue';
import ToastHost from './components/ui/ToastHost.vue';
import AIChatWidget from './components/ui/AIChatWidget.vue';
import OnboardingWizard from './components/ui/OnboardingWizard.vue';
import CrashFallback from './components/pages/CrashFallback.vue';
import Icon from './components/ui/Icon.vue';
import { useUserStore } from './stores/userStore';
import { useWhiteLabelStore } from './stores/whiteLabelStore';
import { hasCrashed, crashDetail } from './stores/crashState';
import { markPrerenderReady, PRERENDER_ASYNC_ROUTES } from './composables/usePrerenderReady';

const userStore = useUserStore();
const whiteLabelStore = useWhiteLabelStore();
const route = useRoute();

provide('userStore', userStore);
provide('whiteLabelStore', whiteLabelStore);

// Browser-based PDF export (core/pdf_export_browser.py) navigates with
// ?pdf=1 in a headless browser — NavBar/AppFooter stay (brand identity in
// the printed PDF), but interactive-only UI never should.
//
// Real bug found live 2026-09-04: a solid ink-900/60 backdrop (the
// OnboardingWizard's) was rendering full-bleed over the entire content
// area of every PDF export's first page. Root cause: this used to be
// `computed(() => route.query.pdf === '1')` -- reactive off Vue Router's
// route object, which is not guaranteed populated by the time this
// component's onMounted fires on a fresh, direct URL navigation (exactly
// what the headless PDF export does -- a real page.goto(), not an
// in-app router-link transition, where the route is already resolved).
// main.js already sets window.__PDF_EXPORT_MODE__ synchronously, at
// module-parse time, before Vue even mounts, specifically to avoid this
// exact class of timing race -- this just wasn't using it. Checked first;
// falls back to the route query for the (currently theoretical) case of
// a client-side navigation into a ?pdf=1 URL without a full page load.
const isPdfMode = computed(() => window.__PDF_EXPORT_MODE__ === true || route.query.pdf === '1');

// Real bug found live 2026-09-05 (owner: the public /shared/:token page
// showed the full authenticated site NavBar -- the viewer's own account
// quotas, own avatar/name -- stacked on top of SharedReportPage.vue's own
// minimal header, reading as a duplicated/broken logo). router/index.js's
// own route definition already declares `meta: { hideNav: true }` for
// this exact route -- the intent was always there, it just had nothing
// reading it. Grepped the whole frontend to confirm: this was the only
// consumer this meta flag ever needed and it had zero, anywhere.
const hideNav = computed(() => route.meta?.hideNav === true);

// Hide chat on processing (distracting), auth screens, and PDF export
const showChat = computed(() => {
  if (isPdfMode.value) return false;
  const hide = ['processing', 'login', 'register', 'forgot-password', 'reset-password'];
  return !hide.includes(route.name);
});

onMounted(async () => {
  // fetchUser() is already called by the router beforeEach guard before
  // navigation completes — calling it again here would set loading=true a
  // second time, causing DashboardPage/ProfilePage/NavBar to flash their
  // loading skeletons and re-render content on every page load.
  if (!userStore.initialized) {
    await userStore.fetchUser();
  }
  // Initialize white-label theming when user is authenticated
  if (userStore.isAuthenticated) {
    await whiteLabelStore.init();
    // Show onboarding for new users who haven't completed it
    if (!isPdfMode.value && !localStorage.getItem('onboarding_completed')) {
      showOnboarding.value = true;
    }
  }

  // Prerender-ready signal (core/prerender.py) — most pages' real content
  // is already in the DOM synchronously, so fire once mounted. Pages that
  // fetch data after mount (Learn, Article) opt out here and fire it
  // themselves once real content — not a loading skeleton — is showing.
  if (!PRERENDER_ASYNC_ROUTES.has(route.name)) {
    nextTick(() => markPrerenderReady());
  }
});

// Onboarding wizard
const showOnboarding = ref(false);

const completeOnboarding = () => {
  localStorage.setItem('onboarding_completed', 'true');
  showOnboarding.value = false;
};

const dismissOnboarding = () => {
  localStorage.setItem('onboarding_completed', 'true');
  showOnboarding.value = false;
};

const handlePageReload = () => {
  pageError.value = null;
  window.location.reload();
};

// Re-init white-label when auth state changes (login/logout)
watch(() => userStore.isAuthenticated, async (authenticated) => {
  if (authenticated) {
    await whiteLabelStore.init();
  } else {
    whiteLabelStore.reset();
  }
});

// Error boundary for page components — when a lazy-loaded page throws
// during render/setup, this catches it locally, preventing the global
// error handler from nuking NavBar/Footer. The router.onError handler
// will also recover from the stuck navigation.
const pageError = ref(null);
onErrorCaptured((err, instance, info) => {
  console.error('App.vue error boundary caught:', err?.message, 'in', instance?.type?.__name || 'unknown', info);
  // Don't kill the app — just show a recoverable error in the main content area
  pageError.value = err?.message || 'Unknown page error';
  // Returning false prevents the error from propagating to app.config.errorHandler
  return false;
});
</script>
